import React, { useRef, useState } from 'react';
import { useStore } from '../../../store/useStore';
import { Palette, Type, BookOpen, Image as ImageIcon, UploadCloud, FileText } from 'lucide-react';
import { Input } from '../../ui/Input';
import { toast } from 'sonner';
import { UploadModal } from '../../ui/UploadModal';

export const IdentidadeVisual: React.FC = () => {
  const { brandingData, setBrandingData } = useStore();
  const [isDocsUploadOpen, setIsDocsUploadOpen] = useState(false);

  const fileInputRefPrincipal = useRef<HTMLInputElement>(null);
  const fileInputRefNegativa = useRef<HTMLInputElement>(null);
  const fileInputRefPositiva = useRef<HTMLInputElement>(null);
  const fileInputRefFavicon = useRef<HTMLInputElement>(null);

  const handleColorChange = (key: keyof typeof brandingData.cores, value: string) => {
    setBrandingData({
      cores: { ...brandingData.cores, [key]: value }
    });
  };

  const handleTypographyChange = (key: keyof typeof brandingData.tipografias, value: string) => {
    setBrandingData({
      tipografias: { ...brandingData.tipografias, [key]: value }
    });
  };

  const handleLogoUpload = (type: 'principal' | 'negativa' | 'positiva' | 'favicon', e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setBrandingData({
        logos: { ...brandingData.logos, [type]: url }
      });
      toast.success(`Logo ${type} atualizada com sucesso!`);
    }
  };

  const handleDocsUpload = (files: File[], title: string) => {
    if (files.length > 0) {
      const newDocs = files.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        name: title ? `${title} - ${file.name}` : file.name,
        size: (file.size / 1024 / 1024).toFixed(1) + ' MB'
      }));
      setBrandingData({
        brandbook: [...(brandingData.brandbook || []), ...newDocs]
      });
      toast.success(`${newDocs.length} documento(s) enviado(s) com sucesso!`);
    }
  };

  return (
    <div className="space-y-8">
      <UploadModal 
        isOpen={isDocsUploadOpen}
        onClose={() => setIsDocsUploadOpen(false)}
        title="Upload de Documentação"
        accept=".pdf,.doc,.docx"
        multiple
        onUpload={handleDocsUpload}
      />
      {/* Hidden File Inputs */}
      <input type="file" accept="image/*" ref={fileInputRefPrincipal} onChange={(e) => handleLogoUpload('principal', e)} className="hidden" />
      <input type="file" accept="image/*" ref={fileInputRefNegativa} onChange={(e) => handleLogoUpload('negativa', e)} className="hidden" />
      <input type="file" accept="image/*" ref={fileInputRefPositiva} onChange={(e) => handleLogoUpload('positiva', e)} className="hidden" />
      <input type="file" accept="image/*" ref={fileInputRefFavicon} onChange={(e) => handleLogoUpload('favicon', e)} className="hidden" />

      <div>
        <h3 className="text-xl font-bold text-gray-900">Identidade Visual & Marca</h3>
        <p className="text-gray-500 mt-1">Defina o &quot;rosto&quot; e a &quot;voz&quot; da sua marca para a IA.</p>
      </div>

      {/* Logotipos */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
          <ImageIcon size={18} className="text-indigo-600" />
          <h4 className="font-semibold text-gray-900">Gestão de Logotipos</h4>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
           {/* Principal */}
           <div onClick={() => fileInputRefPrincipal.current?.click()} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 hover:bg-gray-50 transition-colors cursor-pointer text-center relative overflow-hidden h-40">
             {brandingData.logos?.principal ? (
               <img src={brandingData.logos.principal} alt="Logo Principal" className="w-full h-full object-contain" />
             ) : (
               <>
                 <UploadCloud size={24} className="text-gray-400 mb-2" />
                 <span className="text-sm font-medium text-gray-900">Logo Principal</span>
                 <span className="text-xs text-gray-500 mt-1">Colorida / Oficial</span>
               </>
             )}
           </div>
           {/* Negativa */}
           <div onClick={() => fileInputRefNegativa.current?.click()} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-900 hover:bg-gray-800 transition-colors cursor-pointer text-center relative overflow-hidden h-40">
             {brandingData.logos?.negativa ? (
               <img src={brandingData.logos.negativa} alt="Logo Negativa" className="w-full h-full object-contain" />
             ) : (
               <>
                 <UploadCloud size={24} className="text-gray-400 mb-2" />
                 <span className="text-sm font-medium text-white">Logo Negativa</span>
                 <span className="text-xs text-gray-400 mt-1">Branca p/ fundo escuro</span>
               </>
             )}
           </div>
           {/* Positiva */}
           <div onClick={() => fileInputRefPositiva.current?.click()} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 hover:bg-gray-50 transition-colors cursor-pointer text-center relative overflow-hidden h-40 bg-white">
             {brandingData.logos?.positiva ? (
               <img src={brandingData.logos.positiva} alt="Logo Positiva" className="w-full h-full object-contain" />
             ) : (
               <>
                 <UploadCloud size={24} className="text-gray-400 mb-2" />
                 <span className="text-sm font-medium text-gray-900">Logo Positiva</span>
                 <span className="text-xs text-gray-500 mt-1">Preta p/ fundo claro</span>
               </>
             )}
           </div>
           {/* Favicon */}
           <div onClick={() => fileInputRefFavicon.current?.click()} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 hover:bg-gray-50 transition-colors cursor-pointer text-center relative overflow-hidden h-40">
             {brandingData.logos?.favicon ? (
               <img src={brandingData.logos.favicon} alt="Favicon" className="w-full h-full object-contain" />
             ) : (
               <>
                 <UploadCloud size={24} className="text-gray-400 mb-2" />
                 <span className="text-sm font-medium text-gray-900">Símbolo/Favicon</span>
                 <span className="text-xs text-gray-500 mt-1">Quadrado ou Ícone</span>
               </>
             )}
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Guia de Estilo: Cores */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
            <Palette size={18} className="text-indigo-600" />
            <h4 className="font-semibold text-gray-900">Paleta de Cores (HEX)</h4>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-4">
               <div className="w-10 h-10 rounded-lg shadow-inner border border-gray-200" style={{ backgroundColor: brandingData.cores?.primary || '#e5e7eb' }}></div>
               <Input 
                 placeholder="Ex: #FF0000" 
                 value={brandingData.cores?.primary || ''} 
                 onChange={(e) => handleColorChange('primary', e.target.value)}
                 label="Cor Primária"
               />
            </div>
            <div className="flex items-center gap-4">
               <div className="w-10 h-10 rounded-lg shadow-inner border border-gray-200" style={{ backgroundColor: brandingData.cores?.secondary || '#e5e7eb' }}></div>
               <Input 
                 placeholder="Ex: #00FF00" 
                 value={brandingData.cores?.secondary || ''} 
                 onChange={(e) => handleColorChange('secondary', e.target.value)}
                 label="Cor Secundária"
               />
            </div>
            <div className="flex items-center gap-4">
               <div className="w-10 h-10 rounded-lg shadow-inner border border-gray-200" style={{ backgroundColor: brandingData.cores?.accent || '#e5e7eb' }}></div>
               <Input 
                 placeholder="Ex: #0000FF" 
                 value={brandingData.cores?.accent || ''} 
                 onChange={(e) => handleColorChange('accent', e.target.value)}
                 label="Cor de Destaque / Botões"
               />
            </div>
            <div className="flex items-center gap-4">
               <div className="w-10 h-10 rounded-lg shadow-inner border border-gray-200" style={{ backgroundColor: brandingData.cores?.background || '#e5e7eb' }}></div>
               <Input 
                 placeholder="Ex: #FFFFFF" 
                 value={brandingData.cores?.background || ''} 
                 onChange={(e) => handleColorChange('background', e.target.value)}
                 label="Cor de Fundo"
               />
            </div>
          </div>
        </div>

        {/* Guia de Estilo: Tipografia */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
            <Type size={18} className="text-indigo-600" />
            <h4 className="font-semibold text-gray-900">Tipografia</h4>
          </div>
          <div className="p-6 space-y-6">
            <Input 
              label="Fontes para Títulos (Headings)"
              placeholder="Ex: Montserrat, Inter" 
              value={brandingData.tipografias?.titulos || ''} 
              onChange={(e) => handleTypographyChange('titulos', e.target.value)}
            />
            <Input 
              label="Fontes para Corpo do Texto"
              placeholder="Ex: Open Sans, Roboto" 
              value={brandingData.tipografias?.textos || ''} 
              onChange={(e) => handleTypographyChange('textos', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Documentação Institucional */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2">
          <BookOpen size={18} className="text-indigo-600" />
          <h4 className="font-semibold text-gray-900">Documentação Institucional (Base de Conhecimento Visual)</h4>
        </div>
        <div className="p-6">
           <div onClick={() => setIsDocsUploadOpen(true)} className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-10 hover:bg-gray-50 transition-colors cursor-pointer text-center">
             <UploadCloud size={32} className="text-gray-400 mb-4" />
             <span className="text-base font-medium text-gray-900">Fazer Upload de Arquivos</span>
             <span className="text-sm text-gray-500 mt-2 max-w-md">Envie Brandbook, Manual da Marca, Manifestos ou guias de Tom de Voz. A IA vai ler isso para entender as regras da sua comunicação.</span>
           </div>
           
           {brandingData.brandbook && brandingData.brandbook.length > 0 && (
             <div className="mt-6 space-y-3">
               <h5 className="text-sm font-bold text-gray-700">Documentos Enviados:</h5>
               {brandingData.brandbook.map((doc: any) => (
                 <div key={doc.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                   <div className="w-8 h-8 rounded bg-red-50 text-red-500 flex items-center justify-center shrink-0">
                     <FileText size={16} />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                     <p className="text-xs text-gray-500">{doc.size}</p>
                   </div>
                 </div>
               ))}
             </div>
           )}
        </div>
      </div>
    </div>
  );
};
